import { useCallback, useEffect, useState } from 'react'
import { applyStoredAppTheme, readStoredAppTheme, type AppTheme } from '@/lib/app-theme'

const SESSION_KEY = 'yeshuman-marketing-theme'

export type MarketingThemePreference = 'light' | 'dark' | 'system'
export type MarketingThemeMode = 'light' | 'dark'

function readSession(): MarketingThemePreference | null {
  try {
    const v = sessionStorage.getItem(SESSION_KEY)
    if (v === 'light' || v === 'dark' || v === 'system') return v
  } catch {
    // sessionStorage unavailable
  }
  return null
}

function writeSession(value: MarketingThemePreference) {
  try {
    sessionStorage.setItem(SESSION_KEY, value)
  } catch {
    // sessionStorage unavailable
  }
}

function clearSession() {
  try {
    sessionStorage.removeItem(SESSION_KEY)
  } catch {
    // sessionStorage unavailable
  }
}

function resolvePreference(preference: MarketingThemePreference): MarketingThemeMode {
  if (preference === 'light' || preference === 'dark') return preference
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function applyTheme(theme: MarketingThemeMode) {
  const root = document.documentElement
  root.classList.remove('light', 'dark')
  root.classList.add(theme)
}

function initialPreference(themeToggleEnabled: boolean): MarketingThemePreference {
  if (!themeToggleEnabled) return 'light'
  return readSession() ?? (readStoredAppTheme() as AppTheme)
}

/**
 * Isolated marketing page theme.
 * - When `themeToggleEnabled` is false: forces light, no override.
 * - When enabled: preference from sessionStorage (`light` | `dark` | `system`),
 *   else the stored app preference (including system → OS). Visitor override is
 *   session-only and never touches the app's localStorage preference.
 */
export function useMarketingTheme(themeToggleEnabled: boolean): {
  /** Resolved light/dark used for DOM class + chrome styling. */
  marketingTheme: MarketingThemeMode
  /** Selected preference in the appearance menu. */
  marketingPreference: MarketingThemePreference
  setMarketingTheme: (t: MarketingThemePreference) => void
} {
  const [marketingPreference, setMarketingPreferenceState] = useState<MarketingThemePreference>(() =>
    initialPreference(themeToggleEnabled)
  )
  const [marketingTheme, setMarketingThemeState] = useState<MarketingThemeMode>(() => {
    if (!themeToggleEnabled) return 'light'
    return resolvePreference(initialPreference(true))
  })

  useEffect(() => {
    if (!themeToggleEnabled) {
      applyTheme('light')
      return () => {
        clearSession()
        applyStoredAppTheme()
      }
    }

    const next = resolvePreference(marketingPreference)
    setMarketingThemeState(next)
    applyTheme(next)
    return () => {
      applyStoredAppTheme()
    }
  }, [marketingPreference, themeToggleEnabled])

  useEffect(() => {
    if (!themeToggleEnabled || marketingPreference !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onSystemChange = () => {
      const next = mq.matches ? 'dark' : 'light'
      setMarketingThemeState(next)
      applyTheme(next)
    }
    mq.addEventListener('change', onSystemChange)
    return () => mq.removeEventListener('change', onSystemChange)
  }, [marketingPreference, themeToggleEnabled])

  const setMarketingTheme = useCallback((t: MarketingThemePreference) => {
    writeSession(t)
    setMarketingPreferenceState(t)
  }, [])

  return { marketingTheme, marketingPreference, setMarketingTheme }
}
